import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Admin() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/verifications");
      setQueue(data);
    } catch {
      toast.error("Couldn't load queue");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/discover" replace />;

  const act = async (user_id, action) => {
    setActing((a) => ({ ...a, [user_id]: true }));
    try {
      await api.post(`/admin/verifications/${action}`, { user_id });
      toast.success(`Marked as ${action === "approve" ? "verified" : "rejected"}`);
      setQueue((q) => q.filter((u) => u.user_id !== user_id));
      if (preview?.user_id === user_id) setPreview(null);
    } catch {
      toast.error("Action failed");
    } finally {
      setActing((a) => ({ ...a, [user_id]: false }));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 md:py-14" data-testid="admin-page">
      <div className="mb-10">
        <div className="text-gold-soft tracking-overline text-xs mb-2">ADMIN · VERIFICATION QUEUE</div>
        <h1 className="font-display text-4xl font-light">Members awaiting review.</h1>
      </div>

      {loading ? (
        <div className="text-gold-soft tracking-overline text-xs">LOADING</div>
      ) : queue.length === 0 ? (
        <div className="glass-strong rounded-3xl p-12 text-center" data-testid="admin-empty">
          <BadgeCheck className="mx-auto text-gold mb-4" size={28} />
          <h2 className="font-display text-2xl font-light mb-2">The queue is empty.</h2>
          <p className="text-white/55">Every member is reviewed. Beautifully on top of things.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {queue.map((u) => (
            <motion.div
              key={u.user_id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-strong rounded-3xl overflow-hidden"
              data-testid={`pending-${u.user_id}`}
            >
              <button
                onClick={() => setPreview(u)}
                className="block w-full aspect-square bg-[#0a0a0a] overflow-hidden"
              >
                {u.verification_selfie_url ? (
                  <img src={u.verification_selfie_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">
                    no selfie
                  </div>
                )}
              </button>
              <div className="p-5">
                <div className="font-display text-lg leading-tight">{u.name}</div>
                <div className="text-xs text-white/40 mt-1 truncate">{u.email}</div>
                <div className="mt-2 text-xs text-white/40 tracking-wide">
                  Submitted {u.verification_submitted_at ? new Date(u.verification_submitted_at).toLocaleDateString() : "—"}
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => act(u.user_id, "f")}
                    disabled={acting[u.user_id]}
                    className="flex-1 py-2 rounded-full bg-[#D4AF37] text-[#050505] text-sm font-medium hover:bg-[#E6C96C] disabled:opacity-60 transition-colors"
                    data-testid="approve-btn"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => act(u.user_id, "reject")}
                    disabled={acting[u.user_id]}
                    className="px-4 py-2 rounded-full border border-white/15 text-white/70 hover:border-white/40 text-sm transition-colors"
                    data-testid="reject-btn"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {preview && (
        <button
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="preview-overlay"
        >
          <div className="relative max-w-3xl max-h-full">
            <button className="absolute -top-12 right-0 text-white/60" onClick={() => setPreview(null)}>
              <X size={20} />
            </button>
            {preview.verification_selfie_url && (
              <img src={preview.verification_selfie_url} alt="" className="max-w-full max-h-[80vh] rounded-2xl" />
            )}
          </div>
        </button>
      )}
    </div>
  );
}
